// ─────────────────────────────────────────────────────────────
// Service areas: the Maricopa County cities and towns Pepla works in, plus
// the large unincorporated communities of Sun City, Sun City West and
// Anthem. Wickenburg and Gila Bend were removed in Oct 2026 — both sit far
// outside the Valley and their old URLs 301 to /service-areas in
// netlify.toml. This is no longer "every town in the county", so nothing on
// the site should say that. Each page needs content that is genuinely about that
// place; Google penalizes city pages that only swap the city name.
// When Pepla completes a job somewhere, add a line about it to that
// city's `intro` or `projects` (real jobs are the best local signal).
// ─────────────────────────────────────────────────────────────

// Pepla works out of Phoenix. This is the city center, used only to show a
// rough distance on each city page — it is not an address and none is published.
const BASE = { lat: 33.4484, lng: -112.074 };

export const regions = [
  { key: "west", name: "West Valley" },
  { key: "central", name: "Phoenix & Central Valley" },
  { key: "north", name: "North Valley & Foothills" },
  { key: "northeast", name: "Scottsdale & Northeast Valley" },
  { key: "east", name: "East Valley" },
];

// focus = the services listed first on that city's page (service slugs)
export const serviceAreas = [
  // ── West Valley ────────────────────────────────────────────
  {
    slug: "peoria", name: "Peoria", kind: "city", region: "west",
    lat: 33.5806, lng: -112.2374, zips: ["85345", "85381", "85382", "85383"],
    focus: ["roofing"],
    intro:
      "We work across Peoria, from established neighborhoods south of Bell Road to newer communities like Vistancia that stretch north toward Lake Pleasant. Tile roofs dominate here, and many homes from the 1980s and 1990s are reaching the age where the underlayment beneath the tile needs replacing — which is a very different job, and a very different price, than a full roof replacement.",
    faq: {
      q: "Do you charge for a roof inspection in Peoria?",
      a: "No. Inspections and estimates are free, seven days a week from 7:00 AM to 7:00 PM, shifting with daylight through the year. You get photos of what we found and a written scope whether or not you hire us. Call (602) 834-4707.",
    },
    nearby: ["glendale", "sun-city", "surprise", "el-mirage", "youngtown"],
  },
  {
    slug: "glendale", name: "Glendale", kind: "city", region: "west",
    lat: 33.5387, lng: -112.186, zips: ["85301", "85302", "85304", "85306", "85308", "85310"],
    focus: ["roofing"],
    intro:
      "Glendale borders Peoria to the east, and its housing ranges from mid-century homes near historic downtown to newer builds in north Glendale and around Westgate. Older Glendale roofs often have worn underlayment, patched flashing or more than one layer of shingles, so we inspect closely before quoting.",
    faq: {
      q: "Can you replace the roof on an older Glendale home?",
      a: "Yes. On older homes we check the decking, flashing and any previous roof layers before we quote, so the estimate reflects what the job actually needs rather than surprises found mid-project.",
    },
    nearby: ["peoria", "phoenix", "sun-city", "tolleson", "youngtown"],
  },
  {
    slug: "surprise", name: "Surprise", kind: "city", region: "west",
    lat: 33.6292, lng: -112.368, zips: ["85374", "85378", "85379", "85387", "85388"],
    focus: ["roofing"],
    intro:
      "Surprise has grown quickly into one of the West Valley's largest cities, with many master-planned and HOA communities, including Sun City Grand. Concrete tile is the most common roof here, and the city's older subdivisions are reaching the age where underlayment replacement becomes the main job.",
    faq: {
      q: "Do I need HOA approval to replace my roof in Surprise?",
      a: "Many Surprise neighborhoods have HOA rules on roof color and material. Check your association's guidelines before work starts; we can provide product specs and color samples for your submission.",
    },
    nearby: ["sun-city-west", "el-mirage", "peoria", "youngtown", "sun-city"],
  },
  {
    slug: "sun-city", name: "Sun City", kind: "community", region: "west",
    lat: 33.5975, lng: -112.2718, zips: ["85351", "85373"],
    focus: ["roofing"],
    intro:
      "Sun City, one of the first large active-adult communities in the country, opened in 1960 and sits right next to Peoria. Many homes here date to the 1960s and 1970s, so aging roofs, worn underlayment and leak repairs are common requests.",
    faq: {
      q: "Do you work with homeowners in 55+ communities like Sun City?",
      a: "Yes. We give clear start and finish dates, keep job sites tidy each day, and walk you through the work before and after. Some Sun City properties have deed restrictions on exterior changes, so check with your community association first.",
    },
    nearby: ["peoria", "youngtown", "sun-city-west", "el-mirage", "glendale"],
  },
  {
    slug: "sun-city-west", name: "Sun City West", kind: "community", region: "west",
    lat: 33.662, lng: -112.3413, zips: ["85375"],
    focus: ["roofing"],
    intro:
      "Sun City West is an unincorporated active-adult community northwest of Sun City, developed starting in the late 1970s. Homes of that era are now on their second or third roof cycle, and tile roofs there commonly need the underlayment replaced long before the tile itself gives out.",
    faq: {
      q: "Do I need association approval to re-roof in Sun City West?",
      a: "Usually yes. Community architectural guidelines cover roof material and color, so confirm with your association before work starts. We supply the product specs, color and manufacturer details they ask for.",
    },
    nearby: ["surprise", "sun-city", "el-mirage", "peoria", "youngtown"],
  },
  {
    slug: "el-mirage", name: "El Mirage", kind: "city", region: "west",
    lat: 33.6131, lng: -112.3246, zips: ["85335"],
    focus: ["roofing"],
    intro:
      "El Mirage is a compact city bordered by Surprise, Sun City and Youngtown. Much of its housing went up during the 2000s building boom, which means many roofs are now at the age where tile underlayment and flashing deserve a professional inspection.",
    faq: {
      q: "My El Mirage home was built in the 2000s. Should I have the roof inspected?",
      a: "It's a good idea. Concrete tile can last for decades, but the underlayment beneath it wears out much sooner under Arizona heat. A free inspection tells you whether a repair, an underlayment replacement or nothing at all is needed.",
    },
    nearby: ["surprise", "youngtown", "sun-city", "peoria", "sun-city-west"],
  },
  {
    slug: "youngtown", name: "Youngtown", kind: "town", region: "west",
    lat: 33.5939, lng: -112.3032, zips: ["85363"],
    focus: ["roofing"],
    intro:
      "Youngtown is a small town between Sun City and El Mirage, founded in the 1950s as one of Arizona's earliest planned retirement communities. Its older, modest homes often need targeted repairs such as leak fixes, flashing replacement and broken tile, rather than a full re-roof.",
    faq: {
      q: "Do you take on small repair jobs in Youngtown?",
      a: "Yes. Leak repairs, cracked or slipped tiles, flashing and vent boots are all jobs we handle. We'll tell you honestly whether a repair will do or a replacement makes more sense.",
    },
    nearby: ["sun-city", "el-mirage", "peoria", "surprise", "glendale"],
  },
  {
    slug: "goodyear", name: "Goodyear", kind: "city", region: "west",
    lat: 33.4353, lng: -112.3577, zips: ["85338", "85395"],
    focus: ["roofing"],
    intro:
      "Goodyear takes its name from the Goodyear Tire & Rubber Company, which farmed cotton here in the early 1900s. Today it's one of the West Valley's fastest-growing cities, with master-planned communities like Estrella and many homes now old enough for their original builder-grade underlayment to be failing.",
    faq: {
      q: "My Goodyear home is fairly new — can it already need roof work?",
      a: "It can. Builder-grade felt in this climate often fails well before the tile above it does, and a leak does not wait for the warranty to expire. We inspect free and tell you honestly if nothing needs doing yet.",
    },
    nearby: ["avondale", "litchfield-park", "buckeye", "tolleson"],
  },
  {
    slug: "avondale", name: "Avondale", kind: "city", region: "west",
    lat: 33.4356, lng: -112.3496, zips: ["85323", "85392"],
    focus: ["roofing"],
    intro:
      "Avondale sits along I-10 in the southwest Valley and is home to Phoenix Raceway. Its neighborhoods mix older homes near the historic city center with newer subdivisions to the north, so our work here ranges from monsoon repairs to complete re-roofs.",
    faq: {
      q: "Do you repair monsoon damage in Avondale?",
      a: "Yes. After a storm we inspect for lifted or broken tiles, damaged flashing and leaks, document the damage with photos you can share with your insurer, and make the repairs.",
    },
    nearby: ["goodyear", "tolleson", "litchfield-park", "phoenix", "buckeye"],
  },
  {
    slug: "litchfield-park", name: "Litchfield Park", kind: "city", region: "west",
    lat: 33.4934, lng: -112.358, zips: ["85340"],
    focus: ["roofing"],
    intro:
      "Litchfield Park is a small, established city known for its tree-lined streets and The Wigwam resort. Those mature trees are beautiful, but falling leaves and branches can clog roof valleys and damage tile, so regular roof maintenance matters here more than in most of the Valley.",
    faq: {
      q: "Do mature trees affect my roof in Litchfield Park?",
      a: "They can. Leaves and debris collect in valleys and around vents, trapping moisture, and overhanging branches can crack tile. We clear debris, repair damage and recommend trimming where branches touch the roof.",
    },
    nearby: ["goodyear", "avondale", "glendale", "peoria", "tolleson"],
  },
  {
    slug: "tolleson", name: "Tolleson", kind: "city", region: "west",
    lat: 33.45, lng: -112.2593, zips: ["85353"],
    focus: ["roofing"],
    intro:
      "Tolleson is a small city along I-10 west of Phoenix, with settled residential streets tucked in among the distribution yards along the freeway. The housing here is mostly older stock, which means the work is usually underlayment, flashing and tile repair rather than a full tear-off.",
    faq: {
      q: "My Tolleson house is from the eighties. Does it need a whole new roof?",
      a: "Often not. On a tile roof of that age the tile itself is usually still sound and it is the felt underneath that has given up, so the job is to lift the tile, replace the underlayment and set the tile back down. We inspect for free and tell you which one yours needs.",
    },
    nearby: ["avondale", "phoenix", "glendale", "goodyear"],
  },
  {
    slug: "buckeye", name: "Buckeye", kind: "city", region: "west",
    lat: 33.3703, lng: -112.5838, zips: ["85326", "85396"],
    focus: ["roofing"],
    intro:
      "Buckeye has been one of the fastest-growing cities in the country, with new communities like Verrado spreading across its large land area. A lot of that housing stock is new enough that roofs are still under warranty, and old enough elsewhere in town that underlayment is coming due.",
    faq: {
      q: "Do you cover the newer Buckeye communities?",
      a: "Yes. A lot of Verrado and the newer subdivisions are young enough that the roof may still be covered by a builder or manufacturer warranty, so the first thing we do is check what you already have before quoting anything.",
    },
    nearby: ["goodyear", "avondale", "litchfield-park"],
  },

  // ── Phoenix & Central Valley ───────────────────────────────
  {
    slug: "phoenix", name: "Phoenix", kind: "city", region: "central",
    lat: 33.4484, lng: -112.074, zips: ["85003", "85006", "85013", "85016", "85018", "85021", "85027", "85032", "85048", "85085"],
    focus: ["roofing"],
    intro:
      "Phoenix is Arizona's capital and largest city, and its housing covers every era, from early 1900s homes in central historic districts to newer builds in north Phoenix and Laveen. Flat foam roofs, shingle and tile all appear here, sometimes on the same street.",
    faq: {
      q: "Do I need approval to re-roof a home in a Phoenix historic district?",
      a: "Homes in City of Phoenix historic districts may need historic preservation review before exterior changes, including some roofing work. We'll help you check your property's status before the project starts.",
    },
    nearby: ["glendale", "tempe", "scottsdale", "paradise-valley", "tolleson"],
  },
  {
    slug: "guadalupe", name: "Guadalupe", kind: "town", region: "central",
    lat: 33.3709, lng: -111.9629, zips: ["85283"],
    focus: ["roofing"],
    intro:
      "Guadalupe is a small, close-knit town between Tempe and Phoenix's Ahwatukee area, with deep Yaqui and Mexican-American heritage. Many homes are older and modest in size, so roof repairs, cooling upgrades and practical outdoor improvements are the most common needs.",
    faq: {
      q: "Do you offer free estimates in Guadalupe?",
      a: "Yes. Estimates are free and come with photos of what we found, so you can see the condition of your roof for yourself before deciding anything.",
    },
    nearby: ["tempe", "phoenix", "chandler", "mesa"],
  },

  // ── North Valley & Foothills ───────────────────────────────
  {
    slug: "anthem", name: "Anthem", kind: "community", region: "north",
    lat: 33.8673, lng: -112.146, zips: ["85086"],
    focus: ["roofing"],
    intro:
      "Anthem is an unincorporated master-planned community along I-17, developed from the late 1990s onward. Its homes are now reaching the age where original roofs need attention, particularly the felt under concrete tile.",
    faq: {
      q: "Do Anthem homes need association approval for exterior work?",
      a: "Anthem has community architectural guidelines, so plan to submit roofing plans to your association before work begins. We supply the specs, colors and drawings you need.",
    },
    nearby: ["cave-creek", "phoenix", "peoria", "carefree"],
  },
  {
    slug: "cave-creek", name: "Cave Creek", kind: "town", region: "north",
    lat: 33.8333, lng: -111.9507, zips: ["85331", "85327"],
    focus: ["roofing"],
    intro:
      "Cave Creek is a foothills town north of Phoenix, sitting a bit above 2,000 feet, with a rustic western character and larger desert lots. Tile and foam roofs on larger desert lots are the norm, and wind exposure on the higher parcels shows up as lifted tile and worn flashing.",
    faq: {
      q: "Do you work on the larger desert lots in Cave Creek?",
      a: "Yes. Access and terrain shape how we stage a re-roof up here, and wind exposure on the higher lots is hard on tile and flashing. We walk the property before quoting anything.",
    },
    nearby: ["carefree", "anthem", "scottsdale", "phoenix"],
  },
  {
    slug: "carefree", name: "Carefree", kind: "town", region: "north",
    lat: 33.8223, lng: -111.918, zips: ["85377"],
    focus: ["roofing"],
    intro:
      "Carefree, next to Cave Creek, is a small town of custom homes set among boulders and desert landscape, and is known for its giant sundial. Custom architecture often means flat and low-slope roofs, unusual roof lines and outdoor spaces designed around the views.",
    faq: {
      q: "Can you work on custom homes in Carefree?",
      a: "Yes. Custom homes often have flat foam sections, parapets and unusual roof lines. We inspect each area separately and match materials so repairs blend in with the original design.",
    },
    nearby: ["cave-creek", "scottsdale", "anthem", "fountain-hills"],
  },

  // ── Scottsdale & Northeast Valley ──────────────────────────
  {
    slug: "scottsdale", name: "Scottsdale", kind: "city", region: "northeast",
    lat: 33.4942, lng: -111.9261, zips: ["85250", "85251", "85254", "85255", "85257", "85258", "85259", "85260", "85262", "85266"],
    focus: ["roofing"],
    intro:
      "Scottsdale stretches from established neighborhoods in the south to custom desert homes in the north. Pueblo and Santa Fe-style homes with flat, foam-coated roofs are common here, and they need periodic recoating to stand up to the sun.",
    faq: {
      q: "Can you recoat a flat foam roof in Scottsdale?",
      a: "Yes. Foam roofs rely on their protective coating to block UV damage. We inspect the foam, repair any damaged spots and recoat, or recommend replacement if the foam itself has failed.",
    },
    nearby: ["paradise-valley", "phoenix", "fountain-hills", "tempe", "carefree"],
  },
  {
    slug: "paradise-valley", name: "Paradise Valley", kind: "town", region: "northeast",
    lat: 33.531, lng: -111.9426, zips: ["85253"],
    focus: ["roofing"],
    intro:
      "Paradise Valley is a mostly residential town of luxury custom homes on large lots between Phoenix and Scottsdale. Projects here tend to be high-end tile roof replacements and underlayment work where the tile itself is worth keeping.",
    faq: {
      q: "Are there extra rules for roof work on a hillside lot in Paradise Valley?",
      a: "The Town of Paradise Valley has hillside development regulations, and roof work on those parcels can draw extra review. We check what applies to your lot before scheduling the work.",
    },
    nearby: ["scottsdale", "phoenix", "tempe"],
  },
  {
    slug: "fountain-hills", name: "Fountain Hills", kind: "town", region: "northeast",
    lat: 33.6117, lng: -111.7174, zips: ["85268"],
    focus: ["roofing"],
    intro:
      "Fountain Hills sits against the McDowell Mountains and is known for its landmark fountain in Fountain Park. Hillside homes with mountain views also catch more wind, which is hard on tile and flashing.",
    faq: {
      q: "Is wind damage common on Fountain Hills roofs?",
      a: "More than in the Valley floor. Ridge and hillside homes catch stronger gusts, which lifts tile and works flashing loose over time. If a storm came through, we'll inspect and photograph it for free.",
    },
    nearby: ["scottsdale", "mesa", "carefree"],
  },

  // ── East Valley ────────────────────────────────────────────
  {
    slug: "tempe", name: "Tempe", kind: "city", region: "east",
    lat: 33.4255, lng: -111.94, zips: ["85281", "85282", "85283", "85284"],
    focus: ["roofing"],
    intro:
      "Tempe is home to Arizona State University, and much of the city is built out with older ranch-style homes, many of them rentals. We work with both owner-occupants and landlords on roof repairs, replacements and cooling upgrades.",
    faq: {
      q: "Do you work with landlords and rental properties in Tempe?",
      a: "Yes. We can coordinate access with tenants, send photo reports you can review remotely, and plan work to keep disruption to a minimum.",
    },
    nearby: ["phoenix", "mesa", "chandler", "guadalupe", "scottsdale"],
  },
  {
    slug: "mesa", name: "Mesa", kind: "city", region: "east",
    lat: 33.4152, lng: -111.8315, zips: ["85201", "85203", "85204", "85205", "85206", "85207", "85209", "85210", "85212", "85213"],
    focus: ["roofing"],
    intro:
      "Mesa is Arizona's third-largest city, and its homes range from older central neighborhoods to newer communities in east Mesa such as Eastmark. That range means everything from shingle repairs on older homes to tile underlayment work on the newer builds.",
    faq: {
      q: "Do you serve all of Mesa, including east Mesa?",
      a: "Yes. We serve the whole city, from central Mesa to the newer communities near the eastern edge of town.",
    },
    nearby: ["gilbert", "tempe", "chandler", "apache-junction", "queen-creek"],
  },
  {
    slug: "chandler", name: "Chandler", kind: "city", region: "east",
    lat: 33.3062, lng: -111.8413, zips: ["85224", "85225", "85226", "85248", "85249", "85286"],
    focus: ["roofing"],
    intro:
      "Chandler is a major East Valley tech hub with many planned neighborhoods, including Ocotillo. Homes from the 1990s and 2000s make up much of the city, which puts a lot of Chandler roofs right at the age where underlayment starts to fail.",
    faq: {
      q: "Should I have the roof checked before putting panels on it?",
      a: "Yes. If the roof has only a few years left, taking an array back off later costs far more than doing the roof first. We inspect and tell you whether the roof will outlast the panels — we do the roofing, your solar company handles the panels.",
    },
    nearby: ["gilbert", "tempe", "mesa", "guadalupe"],
  },
  {
    slug: "gilbert", name: "Gilbert", kind: "town", region: "east",
    lat: 33.3528, lng: -111.789, zips: ["85233", "85234", "85295", "85296", "85297", "85298"],
    focus: ["roofing"],
    intro:
      "Gilbert is the largest incorporated town in the United States and a family-focused community with neighborhoods like Agritopia and Val Vista Lakes. Most of its housing went up in a tight window, so whole streets reach the end of their underlayment's life at roughly the same time.",
    faq: {
      q: "My Gilbert neighbors are all replacing their roofs. Do I need to?",
      a: "Maybe not yet, and maybe not a full replacement. Whole streets here went up in the same couple of years, so underlayment reaches the end of its life at roughly the same time — but on a tile roof that often means new felt under your existing tile, not a tear-off. An inspection tells you which.",
    },
    nearby: ["mesa", "chandler", "queen-creek", "tempe"],
  },
  {
    slug: "queen-creek", name: "Queen Creek", kind: "town", region: "east",
    lat: 33.2487, lng: -111.6343, zips: ["85142"],
    focus: ["roofing"],
    intro:
      "Queen Creek is a fast-growing town in the southeast Valley with agricultural roots and larger residential lots. The town spans Maricopa and Pinal counties, and its newer homes mean roof work here is more often storm damage and warranty claims than age.",
    faq: {
      q: "Do you serve the Pinal County side of Queen Creek?",
      a: "Our service area is built around Maricopa County. If your home is on the Pinal County side of Queen Creek, call us at (602) 834-4707 and we'll let you know whether we can take on your project.",
    },
    nearby: ["gilbert", "mesa", "chandler", "apache-junction"],
  },
  {
    slug: "apache-junction", name: "Apache Junction", kind: "city", region: "east",
    lat: 33.415, lng: -111.5496, zips: ["85119", "85120"],
    focus: ["roofing"],
    intro:
      "Apache Junction sits at the foot of the Superstition Mountains. Most of the city is in Pinal County, with a small portion in Maricopa County. Its mix of older homes and manufactured housing often calls for roof repairs and cooling upgrades.",
    faq: {
      q: "Is all of Apache Junction in your service area?",
      a: "The Maricopa County portion is part of our standard service area. For homes elsewhere in Apache Junction, call (602) 834-4707 and we'll confirm whether we can help.",
    },
    nearby: ["mesa", "queen-creek", "gilbert"],
  },
];

// ── helpers ──────────────────────────────────────────────────
const toRad = (d) => (d * Math.PI) / 180;

// Rough driving distance from Phoenix: straight-line miles × 1.25, rounded to 5
export const milesFromBase = (area) => {
  const R = 3958.8;
  const dLat = toRad(area.lat - BASE.lat);
  const dLng = toRad(area.lng - BASE.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(BASE.lat)) * Math.cos(toRad(area.lat)) * Math.sin(dLng / 2) ** 2;
  const straight = 2 * R * Math.asin(Math.sqrt(h));
  return Math.max(5, Math.round((straight * 1.25) / 5) * 5);
};

export const distanceLabel = (area) =>
  area.slug === "phoenix" ? "Home turf" : `About ${milesFromBase(area)} miles`;

export const kindLabel = (area) =>
  area.kind === "community" ? "Unincorporated community" : area.kind === "town" ? "Incorporated town" : "Incorporated city";

export const permitAuthority = (area) =>
  area.kind === "community" ? "Maricopa County" : `${area.kind === "town" ? "Town" : "City"} of ${area.name}`;

export const regionName = (key) => regions.find((r) => r.key === key)?.name || "";

export const getArea = (slug) => serviceAreas.find((a) => a.slug === slug);

// Standard questions (answers use each city's own facts) + the city's own question
export const areaFaqs = (area) => {
  const miles = milesFromBase(area);
  const where =
    area.slug === "phoenix"
      ? "Phoenix is home turf for us"
      : `${area.name} is roughly ${miles} driving miles from Phoenix`;
  const permits =
    area.kind === "community"
      ? `Because ${area.name} is an unincorporated community, building permits go through Maricopa County rather than a city. We confirm what your project requires and handle the permits and inspections for you.`
      : `Requirements depend on the type of work. For ${area.name} projects, we confirm what the ${permitAuthority(area)} requires for your job and handle any permits and inspections for you.`;
  return [
    {
      q: `Does Pepla Roofing serve ${area.name}, AZ?`,
      a: `Yes. ${where}, and ${area.name} is part of our Maricopa County service area. Call (602) 834-4707 or request a free estimate online.`,
    },
    area.faq,
    {
      q: `What roofs do you work on in ${area.name}?`,
      a: `Tile, shingle, flat and foam — the four systems ${area.name} homes actually have. That includes tile underlayment replacement, which is what most "leaking tile roof" calls in the Valley turn out to need.`,
    },
    { q: `Who handles permits for projects in ${area.name}?`, a: permits },
    {
      q: `How much does a roof replacement cost in ${area.name}?`,
      a: `There's no honest flat answer — cost comes down to the roof system, square footage and pitch, the condition of the decking, and whether rooftop equipment has to come off first. We inspect ${area.name} roofs for free and give you an itemized estimate with the manufacturer, line and color named, so you can compare it against anyone else's.`,
    },
    {
      q: `My tile roof is leaking. Do I need a whole new roof?`,
      a: `Usually not. On Valley tile roofs the tile commonly outlasts the underlayment beneath it, and that underlayment is what actually keeps water out. Replacing it in the affected areas and resetting your existing tile fixes a lot of ${area.name} leaks without a full replacement.`,
    },
  ];
};
